import React, { useContext, useEffect, useMemo, useState } from 'react'

import { FilterContext } from '../../context/filterContext'
import { useDebounce } from '../../hooks/useDebounce'
import { getPaginationQueryVariables } from '../../utils/getPaginationQueryVariables'

import ListView, { ListViewProps } from './ListView'

type ServerSideListViewProps<
  QueryDataType,
  VariablesType extends { filter?: object },
> = ListViewProps & {
  aggregationIndex: keyof QueryDataType
  breakpoint?: number
  defaultPageSize?: number
  variables?: VariablesType
  refetchRef?: React.ForwardedRef<unknown>
  ignoreRouteFilters?: boolean
  onTotalCountChanged?: (count: number) => void
  customEmptyComponent?: React.ReactNode
}

function ServerSideListView<
  QueryDataType,
  VariablesType extends { filter?: object },
>({
  aggregationIndex,
  breakpoint = 100,
  defaultPageSize = 20,
  variables,
  refetchRef,
  ignoreRouteFilters = false,
  onTotalCountChanged,
  customEmptyComponent,
  ...rest
}: ServerSideListViewProps<QueryDataType, VariablesType>) {
  const { updateFilters, routeFilters } = useContext(FilterContext)
  const [totalCount, setTotalCount] = useState<number>()
  const [searchString, setSearchString] = useState<string>()
  const debouncedSearchString = useDebounce(searchString, 300)

  const { filter, ...restVariables } = variables || {}

  const useServerSidePagination = useMemo(
    () => Boolean(routeFilters?.extra?.useServerSidePagination),
    [routeFilters?.extra?.useServerSidePagination]
  )

  useEffect(() => {
    if (useServerSidePagination && debouncedSearchString?.length > 0) {
      updateFilters({ pagination: { current: 1 } })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchString])

  const queryVariables = useMemo(() => {
    const useFilters =
      !ignoreRouteFilters && useServerSidePagination && routeFilters.filters

    return {
      filter: {
        ...(filter || {}),
        ...(useFilters ? routeFilters.filters : {}),
      },
      ...getPaginationQueryVariables(
        routeFilters,
        debouncedSearchString,
        breakpoint,
        useServerSidePagination
      ),
      ...restVariables,
    }
  }, [
    filter,
    ignoreRouteFilters,
    useServerSidePagination,
    routeFilters,
    debouncedSearchString,
    breakpoint,
    restVariables,
  ])

  const handleData = (data) => {
    const totalCount = data?.[aggregationIndex]?.totalCount ?? 0
    setTotalCount(totalCount)
    onTotalCountChanged?.(totalCount)

    const shouldUseServerSide =
      data?.[aggregationIndex]?.totalCount >= breakpoint

    if (
      typeof routeFilters?.extra?.useServerSidePagination === 'undefined' ||
      (routeFilters?.extra?.useServerSidePagination === false &&
        shouldUseServerSide)
    ) {
      updateFilters({
        extra: {
          useServerSidePagination: shouldUseServerSide,
        },
      })
    }
  }

  return (
    <ListView
      {...rest}
      variables={queryVariables}
      setQueryDataCallback={handleData}
      setSearchInput={useServerSidePagination ? setSearchString : undefined}
      ref={refetchRef}
      pagination={{
        defaultPageSize,
        total: useServerSidePagination ? totalCount : undefined,
        showSizeChanger: true,
        ...(routeFilters.pagination
          ? {
              current: routeFilters.pagination.current,
            }
          : {}),
        hideOnSinglePage: true,
      }}
      serverSidePagination={useServerSidePagination}
      customEmptyComponent={customEmptyComponent}
    />
  )
}

export default ServerSideListView
